/**
 * Integrytas Group - Concurrency Manager (CMM)
 * Page: CMM.LOCKS
 *
 * Configure which entities are soft-lock managed (+ lease TTL), and monitor /
 * force-release live record leases.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'CMM.LOCKS';

interface ManagedRow { cmm_managed_entity_id: number; entity_name: string; lease_ttl_seconds: number; is_active: 'Y' | 'N' }
interface LeaseRow { cmm_lease_id: number; entity_name: string; record_id: string; holder_user_id: number | null; holder_name: string | null; acquired_at: string; expires_at: string }

const LEASE_GRID_COLS: GridColumnDef[] = [
  { key: 'record',     header: 'Record',  defaultWidth: 240 },
  { key: 'holder',     header: 'Held by', defaultWidth: 180 },
  { key: 'acquired_at', header: 'Since',  defaultWidth: 200 },
  { key: 'expires_at', header: 'Expires', defaultWidth: 140 },
];

export interface CmmLocksPageProps { session: AuthSession }

export function CmmLocksPage({ session }: CmmLocksPageProps) {
  return (
    <PersonalizationProvider pageCode="CMM.LOCKS" session={session}>
      <CmmLocksPageInner session={session} />
    </PersonalizationProvider>
  );
}

function CmmLocksPageInner({ session }: CmmLocksPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [managed, setManaged] = useState<ManagedRow[]>([]);
  const [leases, setLeases] = useState<LeaseRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets. entity/holder filter the active-leases
  // list; active filters the lease-managed entities list (§17.B12 — default to
  // Active-only on first load).
  const [facets, setFacets] = useState<{ entity: string[]; holder: string[]; active: string[] }>({ entity: [], holder: [], active: ['Y'] });

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [m, l] = await Promise.all([
        apiDb.from('cmm_managed_entity').select('cmm_managed_entity_id, entity_name, lease_ttl_seconds, is_active').order('entity_name'),
        apiDb.from('cmm_lease').select('cmm_lease_id, entity_name, record_id, holder_user_id, holder_name, acquired_at, expires_at').order('expires_at', { ascending: false }).limit(100),
      ]);
      if (m.error) throw m.error;
      setManaged((m.data ?? []) as ManagedRow[]);
      setLeases(((l.data ?? []) as LeaseRow[]).filter(r => new Date(r.expires_at) > new Date()));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  // Distinct facet options derived from the loaded leases (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      entity: uniq(leases.map(l => l.entity_name)).map(v => ({ value: v, label: v })),
      holder: uniq(leases.map(l => l.holder_name ?? '')).map(v => ({ value: v, label: v })),
      // §17.B12: Active/Inactive facet for the lease-managed entities list.
      active: [
        { value: 'Y', label: t('common.boolean.active', 'Active') },
        { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
      ],
    };
  }, [leases, t]);

  const anyFilterActive = filter.trim().length > 0 || facets.entity.length > 0 || facets.holder.length > 0 || facets.active.length > 0;
  // Clear-all resets every facet to All (never back to the ['Y'] default).
  const clearAll = () => { setFilter(''); setFacets({ entity: [], holder: [], active: [] }); };

  // Lease-managed entities filtered by the Active facet (§17.B12).
  const filteredManaged = useMemo(
    () => managed.filter(m => facets.active.length === 0 || facets.active.includes(m.is_active)),
    [managed, facets.active],
  );

  const filteredLeases = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return leases.filter(l => {
      const matchesText = !q ||
        l.entity_name.toLowerCase().includes(q) ||
        l.record_id.toLowerCase().includes(q) ||
        (l.holder_name ?? '').toLowerCase().includes(q);
      // OR within a facet, AND across facets.
      const matchesFacets =
        (facets.entity.length === 0 || facets.entity.includes(l.entity_name)) &&
        (facets.holder.length === 0 || facets.holder.includes(l.holder_name ?? ''));
      return matchesText && matchesFacets;
    });
  }, [leases, filter, facets]);

  // TODO: wire relations — an active lease is a flat monitor row (its entity, holder
  // and timing are already shown inline); there is no child collection to surface, so
  // the disclosure renders inert (spacer).
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const addEntity = async () => { await routedInsert(session, 'cmm_managed_entity', { entity_name: '', lease_ttl_seconds: 300, is_active: 'Y' }); await reload(); };
  const saveEntity = async (m: ManagedRow) => { await routedUpdate(session, 'cmm_managed_entity', 'cmm_managed_entity_id', m.cmm_managed_entity_id, { entity_name: m.entity_name, lease_ttl_seconds: m.lease_ttl_seconds, is_active: m.is_active }); };
  const delEntity = async (id: number) => { await routedDelete(session, 'cmm_managed_entity', 'cmm_managed_entity_id', id); setManaged(ms => ms.filter(x => x.cmm_managed_entity_id !== id)); };

  const forceRelease = async (l: LeaseRow) => {
    if (!confirm(t('cmm.confirm.forceRelease', 'Force-release the lease on {{entity}} #{{record}} held by {{holder}}?', { entity: l.entity_name, record: l.record_id, holder: l.holder_name ?? '—' }))) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      await apiDb.rpc('cmm_release', { p_entity: l.entity_name, p_record: l.record_id });
      setNotice(t('cmm.notice.released', 'Lease released.')); await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('cmm.title', 'Concurrency & Locks')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('cmm.placeholder.filter', 'Filter leases…')}
          className="h-7 w-60 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />
        <FacetFilter label={t('cmm.col.entity', 'Entity')} options={facetOptions.entity} selected={facets.entity} onChange={v => setFacets(f => ({ ...f, entity: v }))} />
        <FacetFilter label={t('cmm.col.holder', 'Held by')} options={facetOptions.holder} selected={facets.holder} onChange={v => setFacets(f => ({ ...f, holder: v }))} />
        <FacetFilter label={t('common.boolean.active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
        <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('cmm.refresh', 'Refresh')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('cmm.working', 'Working…')}</span>}
        {notice && <span className="text-csm-xs text-primary">{notice}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      <div className="flex-1 overflow-auto p-3">
        {error && (
          <ErrorBanner
            message={t('cmm.load_failed', "Couldn't load locks and leases.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <div className="mb-4 rounded border border-csm-border bg-white p-3">
          <div className="mb-2 flex items-center gap-2">
            <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('cmm.section.managed', 'Lease-Managed Entities')}</span>
            {canAdd && (<button onClick={() => void addEntity()} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('cmm.add', '+ Add')}</button>)}
          </div>
          {filteredManaged.map((m) => (
            <div key={m.cmm_managed_entity_id} className="mb-1 flex flex-wrap items-center gap-1">
              <input value={m.entity_name} onChange={e => setManaged(ms => ms.map(x => x.cmm_managed_entity_id === m.cmm_managed_entity_id ? { ...x, entity_name: e.target.value } : x))} onBlur={() => void saveEntity(m)} placeholder={t('cmm.placeholder.entity', 'entity (table)')} className="h-7 w-56 rounded border border-csm-border px-1 font-mono text-csm-xs" />
              <span className="text-csm-2xs text-csm-watermark">{t('cmm.ttl', 'TTL')}</span>
              <input type="number" value={m.lease_ttl_seconds} onChange={e => setManaged(ms => ms.map(x => x.cmm_managed_entity_id === m.cmm_managed_entity_id ? { ...x, lease_ttl_seconds: Number(e.target.value) } : x))} onBlur={() => void saveEntity(m)} className="h-7 w-20 rounded border border-csm-border px-1 text-csm-xs" />
              <span className="text-csm-2xs text-csm-watermark">{t('cmm.seconds', 's')}</span>
              <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={m.is_active === 'Y'} onChange={e => { const next: ManagedRow = { ...m, is_active: e.target.checked ? 'Y' : 'N' }; setManaged(ms => ms.map(x => x.cmm_managed_entity_id === m.cmm_managed_entity_id ? next : x)); void saveEntity(next); }} /> {t('cmm.active', 'active')}</label>
              {canDelete && (<button onClick={() => void delEntity(m.cmm_managed_entity_id)} className="text-csm-danger text-csm-xs">{t('cmm.del', 'del')}</button>)}
            </div>
          ))}
          {filteredManaged.length === 0 && (
            <div className="text-csm-2xs text-csm-watermark">
              {managed.length === 0
                ? t('cmm.empty.managed', 'No entities are lease-managed. Add a table to enable soft-locking + backend enforcement for it.')
                : t('cmm.empty.managed_filtered', 'No lease-managed entities match the current filter.')}
            </div>
          )}
        </div>

        <div className="rounded border border-csm-border bg-white p-3">
          <div className="mb-2 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('cmm.section.leases', 'Active Leases')}</div>
          <PersonalizedGrid componentKey="grid" columns={LEASE_GRID_COLS}>
            {(visibleCols, widths) => (
              <table className={GRID_TABLE_CLASS}>
                <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                  <tr>
                    <th className="w-7 border-b border-csm-border px-1 py-1" title={t('cmm.related.header', 'Related records')} />
                    {visibleCols.map(col => (
                      <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t(`cmm.col.${col.key}`, col.header)}</th>
                    ))}
                    <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('cmm.col.action', 'Action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeases.map(l => (
                    <Fragment key={l.cmm_lease_id}>
                    <tr className="hover:bg-csm-surface-alt">
                      <td className="border-b border-csm-border-soft px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                        <RelatedExpandButton
                          open={detail.isOpen(l.cmm_lease_id)}
                          hasChildren={hasRelated(l.cmm_lease_id)}
                          onToggle={() => detail.toggle(l.cmm_lease_id)}
                          expandLabel={t('cmm.related.expand', 'Show related records')}
                          collapseLabel={t('cmm.related.collapse', 'Hide related')}
                          tooltip={t('cmm.related.tooltip', 'Show related records')}
                        />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="border-b border-csm-border-soft px-2 py-1"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'record'      && <span className="font-mono text-csm-xs">{l.entity_name}#{l.record_id}</span>}
                          {col.key === 'holder'      && <span className="text-csm-xs">{l.holder_name ?? '—'}</span>}
                          {col.key === 'acquired_at' && <span className="text-csm-2xs text-csm-watermark">{new Date(l.acquired_at).toLocaleString()}</span>}
                          {col.key === 'expires_at'  && <span className="text-csm-2xs text-csm-watermark">{new Date(l.expires_at).toLocaleTimeString()}</span>}
                        </td>
                      ))}
                      <td className="border-b border-csm-border-soft px-2 py-1 text-right">{canChange && (<button onClick={() => void forceRelease(l)} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-2xs text-csm-danger">{t('cmm.forceRelease', 'Force release')}</button>)}</td>
                    </tr>
                    {detail.isOpen(l.cmm_lease_id) && (
                      <tr>
                        <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                          <RelatedChildrenPanel groups={buildRelatedGroups(l.cmm_lease_id)} session={session} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                  {filteredLeases.length === 0 && !busy && <tr><td colSpan={visibleCols.length + 2} className="px-2 py-3"><EmptyState variant={anyFilterActive ? 'no-match' : 'all-clear'} title={t('cmm.empty.leases', 'No active leases.')} /></td></tr>}
                </tbody>
              </table>
            )}
          </PersonalizedGrid>
        </div>
      </div>
    </div>
  );
}
